import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { useService } from '../contexts/serviceContext'
import editabletypesConstants from '../constants/editabletypes.constants'
import { format, isValid, parse } from 'date-fns'
import {
    checkNameIsValid,
    checkInputTypeIdIsValid,
    checkTestDataIsValid,
} from '../services/editableregion.service'

import AdminCard from './adminCard'
import HtmlEditor from './editableDialogs/HtmlEditor'
import PropTypes from 'prop-types'

const useStyles = makeStyles(theme => ({
    formControl: {
        marginBottom: '20px',
        width: '100%',
    },
    textFieldRoot: {
        '& .MuiInputBase-root.Mui-disabled': {
            color: theme.palette.text.primary,
        },
        '& .MuiInputLabel-root.Mui-disabled': {
            color: theme.palette.text.primary,
        },
        '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.primary,
        },
    },
    formatStyle: {
        marginTop: 8,
        marginBottom: 8,
    },
    previewText: {
        marginTop: theme.spacing(1.25),
    },
    previewValid: {
        color: 'green',
    },
    previewInvalid: {
        color: 'red',
    },
}))

const dateFormatOptions = [
    { value: 'dd/MM/yyyy', label: '27/01/2025 (default)' },
    { value: 'do MMMM yyyy', label: '27th January 2025' },
    { value: 'MM/dd/yyyy', label: '01/27/2025' },
    { value: 'MMMM d, yyyy', label: 'January 27, 2025' },
    { value: 'EEEE, do MMMM yyyy', label: 'Tuesday, 27th January 2025' },
]

const timeFormatOptions = [
    { value: 'HH:mm', label: '24-hour (13:45) (default)' },
    { value: 'hh:mm a', label: '12-hour (1:45 PM)' },
]

const EditableRegionFormOverview = ({
    name,
    description,
    inputTypeId,
    nameChanged,
    descriptionChanged,
    testData,
    testDataChanged,
    minLength,
    maxLength,
    typeChanged,
    isEditMode,
    isMandatory,
    isMandatoryChanged,
    isArchived,
    isArchivedChanged,
    currentEditables,
    inputCustomFormat,
    inputCustomFormatChanged,
}) => {
    const classes = useStyles()
    const { serviceConfig } = useService()

    const [testDataIsValid, setTestDataIsValid] = useState(false)
    const [nameIsValid, setNameIsValid] = useState(false)
    const [inputTypeIdIsValid, setInputTypeIdIsValid] = useState(false)
    const [descriptionIsDisabled, setDescriptionIsDisabled] = useState(false)
    const [preview, setPreview] = useState('')
    const isDatePicker = inputTypeId === editabletypesConstants.DatePicker
    const formatOptions = isDatePicker ? dateFormatOptions : timeFormatOptions
    const selectedValue = inputCustomFormat ?? formatOptions[0].value

    const predefinedOptions =
        inputTypeId === editabletypesConstants.DatePicker
            ? dateFormatOptions.map(o => o.value)
            : timeFormatOptions.map(o => o.value)

    const isCustom =
        inputCustomFormat && !predefinedOptions.includes(inputCustomFormat)

    const [allowCustomFormat, setAllowCustomFormat] = useState(isCustom)

    useEffect(() => {
        setInputTypeIdIsValid(checkInputTypeIdIsValid(inputTypeId))
        setDescriptionIsDisabled(inputTypeId === editabletypesConstants.Address)
    }, [inputTypeId])

    useEffect(() => {
        const nameValidity = checkNameIsValid(name, currentEditables)
        setNameIsValid(nameValidity.isValid)
        setNameValidationMessage(nameValidity.message)
    }, [name, currentEditables])

    useEffect(() => {
        const testDataValidity = checkTestDataIsValid(
            testData,
            minLength,
            maxLength,
            inputTypeId
        )
        setTestDataIsValid(testDataValidity.isValid)
        setTestDataValidationMessage(testDataValidity.message)
    }, [testData, minLength, maxLength])

    useEffect(() => {
        if (allowCustomFormat && inputCustomFormat) {
            try {
                const formatted = format(new Date(), inputCustomFormat)

                const parsed = parse(formatted, inputCustomFormat, new Date())

                if (!isValid(parsed)) {
                    setPreview('Invalid format')
                } else {
                    setPreview(formatted)
                }
            } catch {
                setPreview('Invalid format')
            }
        } else {
            setPreview('')
        }
    }, [allowCustomFormat, inputCustomFormat])

    const [testDataValidationMessage, setTestDataValidationMessage] = useState(
        ''
    )
    const [nameValidationMessage, setNameValidationMessage] = useState('')
    const [
        inputTypeIdValidationMessage,
        setInputTypeIdValidationMessage,
    ] = useState('')

    useEffect(() => {
        setInputTypeIdValidationMessage(
            inputTypeIdIsValid ? 'Valid Entry' : 'Must select an option'
        )
    }, [inputTypeIdIsValid])

    const CustomFormatHelper = () => (
        <span>
            Use{' '}
            <a
                href='https://date-fns.org/v2.30.0/docs/format'
                target='_blank'
                rel='noopener noreferrer'
            >
                date-fns format guide
            </a>{' '}
            for custom formats.
        </span>
    )

    return (
        <AdminCard title={'Overview'}>
            <div>
                <FormControl
                    className={classes.formControl}
                    error={!nameIsValid}
                >
                    <TextField
                        id='name-input'
                        aria-label='name-input'
                        className={classes.textFieldRoot}
                        disabled={isEditMode}
                        type='text'
                        label='Name'
                        value={name}
                        onChange={e => nameChanged(e.target.value)}
                    />
                    <FormHelperText>{nameValidationMessage}</FormHelperText>
                </FormControl>
            </div>
            <div>
                <FormControl className={classes.formControl}>
                    <TextField
                        id='description-input'
                        aria-label='Overview description'
                        type='text'
                        label='Description'
                        disabled={descriptionIsDisabled}
                        value={!descriptionIsDisabled ? description : 'N/A'}
                        onChange={e => descriptionChanged(e.target.value)}
                    />
                </FormControl>
            </div>
            <div>
                <FormControl
                    className={classes.formControl}
                    error={!testDataIsValid}
                >
                    {inputTypeId === editabletypesConstants.HtmlEditor ||
                    inputTypeId === editabletypesConstants.Carousel ? (
                        <HtmlEditor
                            id='html-input'
                            description='Test Data (this only appears in the template preview)'
                            initialHtml={testData || ''}
                            onChange={newHtml => testDataChanged(newHtml)}
                            fontSize={serviceConfig.fontSize}
                            fontFamily={serviceConfig.fontFamily}
                        />
                    ) : (
                        <TextField
                            id='test-data-input'
                            type='text'
                            label='Test Data (this only appears in the template preview)'
                            value={testData || ''}
                            onChange={e => testDataChanged(e.target.value)}
                        />
                    )}
                    <FormHelperText>{testDataValidationMessage}</FormHelperText>
                </FormControl>
            </div>
            <div>
                <FormControl
                    className={classes.formControl}
                    error={!inputTypeIdIsValid}
                >
                    <InputLabel>Type</InputLabel>
                    <Select
                        displayEmpty
                        renderValue={
                            inputTypeId ? undefined : () => 'Please Select'
                        }
                        disabled={isEditMode}
                        value={inputTypeId}
                        onChange={e => typeChanged(e.target.value)}
                    >
                        <MenuItem value={editabletypesConstants.FreeText}>
                            Free Text
                        </MenuItem>
                        <MenuItem value={editabletypesConstants.DatePicker}>
                            Date Picker
                        </MenuItem>
                        <MenuItem value={editabletypesConstants.TimePicker}>
                            Time Picker
                        </MenuItem>
                        <MenuItem value={editabletypesConstants.Dropdown}>
                            Drop Down
                        </MenuItem>
                        <MenuItem value={editabletypesConstants.HtmlEditor}>
                            Html Editor
                        </MenuItem>
                        <MenuItem value={editabletypesConstants.Carousel}>
                            Carousel
                        </MenuItem>
                    </Select>
                    <FormHelperText>
                        {inputTypeIdValidationMessage}
                    </FormHelperText>
                </FormControl>
            </div>
            {(inputTypeId === editabletypesConstants.DatePicker ||
                inputTypeId === editabletypesConstants.TimePicker) && (
                <FormControl className={classes.formControl}>
                    <InputLabel aria-label='Format style' shrink>
                        Format style
                    </InputLabel>
                    <FormControlLabel
                        aria-label='Checkbox custom date format'
                        className={classes.formatStyle}
                        control={
                            <Checkbox
                                checked={allowCustomFormat}
                                onChange={e =>
                                    setAllowCustomFormat(e.target.checked)
                                }
                                color='primary'
                            />
                        }
                        label='Custom format'
                    />

                    {allowCustomFormat ? (
                        <>
                            <TextField
                                aria-label='Enter custom format'
                                value={inputCustomFormat ?? ''}
                                onChange={e =>
                                    inputCustomFormatChanged(
                                        e.target.value || null
                                    )
                                }
                                placeholder='Enter custom format'
                                fullWidth
                                helperText={<CustomFormatHelper />}
                            />
                            {preview && (
                                <div
                                    className={`${classes.previewText} ${
                                        preview === 'Invalid format'
                                            ? classes.previewInvalid
                                            : classes.previewValid
                                    }`}
                                >
                                    Preview: {preview}
                                </div>
                            )}
                        </>
                    ) : (
                        <Select
                            aria-label='Select a pre defined value'
                            value={selectedValue}
                            onChange={e =>
                                inputCustomFormatChanged(e.target.value || null)
                            }
                        >
                            {formatOptions.map(option => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </FormControl>
            )}

            <div>
                <FormControl className={classes.formControl}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isMandatory}
                                onChange={e =>
                                    isMandatoryChanged(e.target.checked)
                                }
                                color='primary'
                            />
                        }
                        label='Is Mandatory?'
                    />
                </FormControl>
            </div>
            {isEditMode ? (
                <div>
                    <FormControl className={classes.formControl}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isArchived}
                                    onChange={e =>
                                        isArchivedChanged(e.target.checked)
                                    }
                                    color='primary'
                                />
                            }
                            label='Archived?'
                        />
                    </FormControl>
                </div>
            ) : null}
        </AdminCard>
    )
}
EditableRegionFormOverview.propTypes = {
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    inputTypeId: PropTypes.number.isRequired,
    nameChanged: PropTypes.func.isRequired,
    descriptionChanged: PropTypes.func.isRequired,
    testData: PropTypes.string,
    testDataChanged: PropTypes.func.isRequired,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    typeChanged: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool.isRequired,
    isMandatory: PropTypes.bool.isRequired,
    isMandatoryChanged: PropTypes.func.isRequired,
    isArchived: PropTypes.bool.isRequired,
    isArchivedChanged: PropTypes.func.isRequired,
    currentEditables: PropTypes.array.isRequired,
    inputCustomFormat: PropTypes.string,
    inputCustomFormatChanged: PropTypes.func,
}

export default EditableRegionFormOverview
